/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {fileViewedStateAtom, toggleFileViewedAtom} from './jotai';
import scrollToFileElement from './scrollToFileElement';
import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react';
import {Box, Text, Tooltip} from '@primer/react';
import {useAtomValue, useSetAtom} from 'jotai';
import React, {useCallback} from 'react';

export function FileHeader({
  path,
  oldPath,
  open,
  onChangeOpen,
  linesAdded,
  linesRemoved,
  isPullRequest,
}: {
  path: string;
  oldPath?: string;
  open?: boolean;
  onChangeOpen?: (open: boolean) => void;
  linesAdded?: number;
  linesRemoved?: number;
  isPullRequest?: boolean;
}) {
  // Even though the enclosing <SplitDiffView> will have border-radius set, we
  // have to define it again here or things don't look right.
  const color = 'fg.muted';

  const pathSeparator = '/';
  const pathParts = path.split(pathSeparator);

  const showStats = (linesAdded != null && linesAdded > 0) || (linesRemoved != null && linesRemoved > 0);

  const filePathParts = (
    <Text fontFamily="mono" fontSize={12} sx={{flexGrow: 1, color: '#ecf0f1'}}>
      {pathParts.reduce((acc, part, idx) => {
        // Nest path parts in a particular way so we can use plain CSS
        // hover selectors to underline nested sub-paths.
        const pathSoFar = pathParts.slice(idx).join(pathSeparator);
        return (
          <span className="file-header-copyable-path" key={idx}>
            {acc}
            <Tooltip
              aria-label={`Copy ${pathSoFar}`}
              direction="se"
              className="file-header-path-element">
              <span
                onClick={() => {
                  navigator.clipboard.writeText(pathSoFar);
                }}>
                {part}
                {idx < pathParts.length - 1 ? pathSeparator : ''}
              </span>
            </Tooltip>
          </span>
        );
      }, <span />)}
    </Text>
  );

  return (
    <Box
      className="split-diff-view-file-header"
      display="flex"
      alignItems="center"
      color={color}
      paddingX={2}
      paddingY={1}
      lineHeight={2}
      sx={{
        bg: '#34495e',
        color: '#ecf0f1',
        borderTopRightRadius: 2,
        borderTopLeftRadius: 2,
        borderBottom: '1px solid',
        borderColor: 'border.default',
        position: 'sticky',
        top: 0,
        zIndex: 2,
      }}>
      {onChangeOpen && (
        <Box
          paddingRight={2}
          onClick={() => {
            onChangeOpen(!open);
            if (open) {
              scrollToFileElement(path);
            }
          }}
          sx={{
            cursor: 'pointer',
            display: 'flex',
            color: '#bdc3c7',
            ':hover': {color: '#ecf0f1'},
          }}>
          {open ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
        </Box>
      )}
      <Box sx={{display: 'flex', flexGrow: 1, alignItems: 'center'}}>
        {oldPath != null && (
          <>
            <Text sx={{color: '#bdc3c7', fontSize: '11px', fontFamily: 'mono', mr: 2}}>
              {oldPath}
            </Text>
            <Text sx={{color: '#bdc3c7', fontSize: '11px', mr: 2}}>→</Text>
          </>
        )}
        {filePathParts}
      </Box>
      {showStats && <DiffStats linesAdded={linesAdded ?? 0} linesRemoved={linesRemoved ?? 0} />}
      {isPullRequest && onChangeOpen && (
        <ViewedCheckbox path={path} onChangeOpen={onChangeOpen} />
      )}
    </Box>
  );
}

function ViewedCheckbox({
  path,
  onChangeOpen,
}: {
  path: string;
  onChangeOpen: (open: boolean) => void;
}): React.ReactElement {
  const viewedState = useAtomValue(fileViewedStateAtom(path));
  const toggleViewed = useSetAtom(toggleFileViewedAtom);
  const isViewed = viewedState === 'VIEWED';

  const onClick = useCallback(() => {
    toggleViewed(path);
    // Collapse when marking as viewed, expand when unmarking
    onChangeOpen(isViewed);

    if (!isViewed) {
      // After collapsing, scroll the file header into view so it's visible
      // below the sticky page header rather than hidden above the viewport.
      scrollToFileElement(path);
    }
  }, [path, isViewed, toggleViewed, onChangeOpen]);

  return (
    <Box
      as="label"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        ml: 2,
        cursor: 'pointer',
        flexShrink: 0,
        userSelect: 'none',
        fontSize: '12px',
        color: isViewed ? '#2ecc71' : '#bdc3c7',
        ':hover': {color: '#ecf0f1'},
      }}>
      <input
        type="checkbox"
        checked={isViewed}
        onChange={onClick}
        style={{cursor: 'pointer', margin: 0}}
      />
      <Text sx={{fontSize: '12px', fontFamily: 'normal'}}>Viewed</Text>
    </Box>
  );
}

function DiffStats({
  linesAdded,
  linesRemoved,
}: {
  linesAdded: number;
  linesRemoved: number;
}): React.ReactElement {
  return (
    <Box display="flex" alignItems="center" sx={{gap: 1, flexShrink: 0, ml: 2}}>
      {linesAdded > 0 && (
        <Text fontFamily="mono" fontSize={11} color="#2ecc71" fontWeight="bold">
          +{linesAdded}
        </Text>
      )}
      {linesRemoved > 0 && (
        <Text fontFamily="mono" fontSize={11} color="#e74c3c" fontWeight="bold">
          -{linesRemoved}
        </Text>
      )}
    </Box>
  );
}
